/**
 * @fileOverview Creates and returns a user object with the right type/properties,
 * guessing whether the new user is a customer, care-receiver, or self-booker.
 */

import CareReceiver, { CareReceiverData } from '@/lib/class/CareReceiver'
import Customer, { CustomerData } from '@/lib/class/Customer'
import SelfBooker, { SelfBookerData } from '@/lib/class/SelfBooker'

type UserData = SelfBookerData | CareReceiverData | CustomerData

const userIsAccountOwner = (
  userData: UserData,
): userData is SelfBookerData | CustomerData =>
  userData.relationship === 'You' || userData.relationship === 'Ich selbst'

function isCareReceiver(userData: UserData): userData is CareReceiverData {
  return 'care_receiver_id' in userData
}

const createUserObject = (userData: UserData) => {
  if (userIsAccountOwner(userData) && isCareReceiver(userData)) {
    return new SelfBooker(userData)
  }

  if (isCareReceiver(userData)) {
    return new CareReceiver(userData)
  }

  if (userIsAccountOwner(userData)) {
    return new Customer(userData)
  }

  throw new Error('Cannot determine user type from given data.')
}

export default createUserObject
